import type { Asset, ServiceType } from "../types";

export const SERVICE_LABELS: Record<ServiceType, string> = {
  life_raft_service: "Life raft service",
  annual_inspection: "Annual inspection",
  five_yearly: "5 yearly inspection",
  load_test: "Load test",
  refurbishment: "Refurbishment",
  hooks_wire_davit_winch: "Hooks / wire / davit / winch",
  wire_rope_change: "Wire rope change",
  winch_parts: "Winch parts",
  hydrostatic_test: "Hydrostatic test",
  sea_trial: "Sea trial",
};

function parseIsoDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function formatDate(value?: string) {
  if (!value) return "No date";
  const date = parseIsoDate(value);
  if (!date) return value;
  return new Intl.DateTimeFormat("en-MY", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function daysUntil(value: string, now = new Date()) {
  const target = parseIsoDate(value);
  if (!target) return Number.POSITIVE_INFINITY;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

export function nearestService(asset: Asset) {
  const entries = Object.entries(asset.services) as Array<
    [ServiceType, NonNullable<Asset["services"][ServiceType]>]
  >;

  return entries
    .filter(([, schedule]) => Boolean(parseIsoDate(schedule.nextService)))
    .sort(
      ([, left], [, right]) =>
        String(left.nextService).localeCompare(String(right.nextService)),
    )
    .map(([serviceType, schedule]) => ({
      serviceType,
      label: SERVICE_LABELS[serviceType],
      date: schedule.nextService,
    }))[0];
}

export type UpcomingAlert = {
  asset: Asset;
  serviceType: ServiceType;
  serviceLabel: string;
  serviceDate: string;
  daysRemaining: number;
  alertWindow: 7 | 14;
};

export function getUpcomingAlerts(assets: Asset[], now = new Date()) {
  return assets
    .flatMap<UpcomingAlert>((asset) => {
      if (asset.status === "Under Service") return [];

      return (Object.entries(asset.services) as Array<
        [ServiceType, NonNullable<Asset["services"][ServiceType]>]
      >).flatMap(([serviceType, schedule]) => {
        if (!schedule.nextService) return [];
        const remaining = daysUntil(schedule.nextService, now);
        if (remaining < 0 || remaining > 14) return [];
        return [
          {
            asset,
            serviceType,
            serviceLabel: SERVICE_LABELS[serviceType],
            serviceDate: schedule.nextService,
            daysRemaining: remaining,
            alertWindow: remaining <= 7 ? 7 : 14,
          },
        ];
      });
    })
    .sort(
      (left, right) =>
        left.daysRemaining - right.daysRemaining ||
        left.asset.assetId.localeCompare(right.asset.assetId),
    );
}
